import EventEmitter from "./eventEmitter";
import environment from "@/enviroments/enviroment";

class HttpClient {
  constructor(baseURL, headers = {}, credentials = "same-origin") {
    this.baseURL = baseURL;
    this.headers = new Headers({
      "Content-Type": "application/json",
      "BrMalls-Subscription-Key-Backend": environment.SUBSCRIPTION_KEY,
      ...headers,
    });
    this.credentials = credentials;
  }

  request(url, settings = {}) {
    let uri = url;

    if (!/^https?:\/\//.test(uri)) {
      uri = `${this.baseURL}${uri}`;
    }

    const { headers: headersCustom = {}, ...otherSettings } = settings;
    const headers = Object.fromEntries(this.headers.entries());

    Object.assign(
      headers,
      headersCustom instanceof Headers
        ? Object.fromEntries(headersCustom.entries())
        : headersCustom,
    );

    const request = new Request(uri, {
      credentials: this.credentials,
      ...otherSettings,
      headers: new Headers(headers),
    });

    return fetch(request);
  }

  async get(path, data, headers = {}) {
    const queryString = data ? `?${new URLSearchParams(data).toString()}` : "";
    const response = await this.request(`${path}${queryString}`, {
      method: "GET",
      headers,
    });

    return this.#handleResponse(response);
  }

  async post(url, data, headers = {}) {
    const response = await this.request(url, {
      method: "POST",
      headers,
      body: JSON.stringify(data),
    });

    return this.#handleResponse(response);
  }

  async patch(url, data, headers = {}) {
    const response = await this.request(url, {
      method: "PATCH",
      headers,
      body: JSON.stringify(data),
    });

    return this.#handleResponse(response);
  }

  sse(url, options = {}) {
    const emitter = new EventEmitter();

    this.request(url, {
      method: "GET",
      ...options,
    })
      .then(async (response) => {
        if (!response.ok) {
          emitter.emit("error", new Error(response.statusText), response);
          emitter.emit("done");
          return;
        }

        const reader = response.body.getReader();

        for await (const data of this.#parseEvents(reader)) {
          emitter.emit(data.name, ...data.props);
        }

        emitter.emit("done", response);
      })
      .catch((error) => {
        emitter.emit(error.name === "AbortError" ? "abort" : "error", error);
        emitter.emit("done");
      });

    return emitter;
  }

  async *#parseEvents(reader) {
    const decoder = new TextDecoder("utf-8");

    try {
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          yield {
            name: "closed",
            props: [new Error("Connection closed by server")],
          };
          break;
        }

        let chunk = decoder.decode(value, { stream: true });
        // Ensure line breaks use LF only so that the regex below works for both LF and CRLF
        chunk = chunk.replace(/\r\n/g, "\n");
        // Normalize previous buffer before concatenation
        buffer = buffer.replace(/\r\n/g, "\n");

        chunk =
          buffer +
          chunk.replace(/([^\n]+)$/, (match, m1 = "") => {
            buffer = m1;

            return "";
          });
        const events = chunk.match(/.*?(?=\n\n)\n\n/gs);

        for (const event of events) {
          if (!event) {
            continue;
          }

          const eventInit = this.#parseEventField(event, "");

          if (eventInit !== null) {
            yield {
              name: "init",
              props: [eventInit],
            };
            continue;
          }

          const eventType = this.#parseEventField(event, "event", "data");
          const eventId = this.#parseEventField(event, "id");
          const eventData = this.#parseEventField(event, "data");

          if (eventData !== null) {
            const data = this.#createBuffer(eventData);

            yield {
              name: eventType,
              props: [data, eventId],
            };
          }
        }
      }
    } catch (error) {
      yield {
        name: "error",
        props: [error],
      };
    }
  }

  #parseEventField(buffer, name, defaultValue = null) {
    // Normalize line breaks to handle both LF and CRLF formats
    const normalized = buffer.replace(/\r?\n/g, "\n");

    let value = defaultValue;

    normalized.replace(
      new RegExp(`(^|\n)${name}: {0,1}([^\n]*)\n`, "g"),
      (match, m1, m2 = "") => {
        if (value === null) {
          value = "";
        }

        value += m2;
      },
    );

    return Object.freeze(value);
  }

  #handleResponse(response) {
    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get("Content-Type");

    if (contentType && contentType.includes("application/json")) {
      return response.json();
    }

    return response.text();
  }

  #createBuffer(data) {
    const buffer = {
      data,
      text() {
        return Promise.resolve(this.data);
      },
      json() {
        try {
          return Promise.resolve(JSON.parse(this.data));
        } catch (e) {
          console.error(e, this.data);
        }

        return Promise.resolve(null);
      },
      toString() {
        return this.data;
      },
      [Symbol.toPrimitive](hint) {
        return hint === "string" ? this.toString() : null;
      },
    };

    return buffer;
  }
}

export default HttpClient;
